<template>
  <div class="date-picker-container" :class="{ 'only-picker': onlyPicker }">
    <VueDatePicker
      v-model="internalValue"
      :range="!onlyOne"
      :inline="onlyPicker"
      :multi-calendars="!onlyOne ? 2 : 0"
      :multi-static="false"
      :enable-time-picker="showTime"
      :max-date="maxDateLimit"
      :min-date="minDateLimit"
      :preset-ranges="!onlyOne ? presetRanges : []"
      :auto-apply="true"
      :locale="locale"
      @update:model-value="handleUpdate"
      @open="onOpen"
      @closed="onClosed"
      :format="format"
      :start-date="startDateView"
      ref="datepicker"
    >
      <template #trigger v-if="!onlyPicker">
        <div class="input-group mb-1">
          <input
            type="text"
            class="form-control not-readonly"
            readonly
            :value="formattedDisplay"
            @click.stop="openPicker"
          />
        </div>
      </template>
    </VueDatePicker>
  </div>
</template>

<script>
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import dateHelper from "../../modules/date";
import dayjs from "dayjs";

export default {
  name: "DatePicker",
  components: { VueDatePicker },
  props: {
    modelValue: [Array, String, Object],
    showPicker: {
      default: false,
      type: Boolean,
    },
    showTime: {
      default: false,
      type: Boolean,
    },
    onlyInRange: Number,
    onlyPicker: {
      default: false,
      type: Boolean,
    },
  },
  emits: ["update:modelValue", "update:showPicker", "change"],
  data() {
    return {
      internalValue: null,
      inString: false,
      onlyOne: false,
      show: this.showPicker,
      isInternalUpdate: false,
    };
  },
  computed: {
    locale() {
      return this.$i18n?.locale || "en";
    },
    maxDateLimit() {
      if (this.onlyInRange === 1) {
        return new Date();
      }
      return null;
    },
    minDateLimit() {
      if (this.onlyInRange === 2) {
        return new Date();
      }
      return null;
    },
    startDateView() {
      if (
        !this.onlyOne &&
        Array.isArray(this.internalValue) &&
        this.internalValue[0]
      ) {
        return this.internalValue[0];
      }
      return null;
    },
    formattedDisplay() {
      if (!this.internalValue) return "";
      if (this.onlyOne) {
        return dateHelper.formatFullName(this.internalValue);
      } else if (
        Array.isArray(this.internalValue) &&
        this.internalValue.length === 2
      ) {
        if (!this.internalValue[0] || !this.internalValue[1]) return "";
        return (
          dateHelper.formatFullName(this.internalValue[0]) +
          " - " +
          dateHelper.formatFullName(this.internalValue[1])
        );
      }
      return "";
    },
    presetRanges() {
      const t = (key) => (this.$t ? this.$t(key) : key);
      return [
        {
          label: t("datePicker.today"),
          range: [
            dayjs().utcOffset(0).startOf("day").toDate(),
            dayjs().utcOffset(0).startOf("day").toDate(),
          ],
        },
        {
          label: t("datePicker.yesterday"),
          range: [
            dayjs().utcOffset(0).subtract(1, "day").startOf("day").toDate(),
            dayjs().utcOffset(0).subtract(1, "day").startOf("day").toDate(),
          ],
        },
        {
          label: t("datePicker.last7Days"),
          range: [
            dayjs().utcOffset(0).subtract(6, "day").startOf("day").toDate(),
            dayjs().utcOffset(0).startOf("day").toDate(),
          ],
        },
        {
          label: t("datePicker.last14Days"),
          range: [
            dayjs().utcOffset(0).subtract(13, "day").startOf("day").toDate(),
            dayjs().utcOffset(0).startOf("day").toDate(),
          ],
        },
        {
          label: t("datePicker.last30Days"),
          range: [
            dayjs().utcOffset(0).subtract(29, "day").startOf("day").toDate(),
            dayjs().utcOffset(0).startOf("day").toDate(),
          ],
        },
        {
          label: t("datePicker.thisMonth"),
          range: [
            dayjs().utcOffset(0).startOf("month").toDate(),
            dayjs().utcOffset(0).startOf("day").toDate(),
          ],
        },
        {
          label: t("datePicker.thisYear"),
          range: [
            dayjs().utcOffset(0).startOf("year").toDate(),
            dayjs().utcOffset(0).startOf("day").toDate(),
          ],
        },
        {
          label: t("datePicker.lifetime"),
          range: [
            dayjs().utcOffset(0).subtract(100, "year").toDate(),
            dayjs().utcOffset(0).startOf("day").toDate(),
          ],
        },
      ];
    },
  },
  watch: {
    modelValue: {
      handler(val) {
        this.parseModelValue(val);
      },
      immediate: true,
    },
    showPicker(val) {
      if (val) {
        if (!this.show) {
          this.openPicker();
        }
      } else {
        if (this.show) {
          this.closePicker();
        }
      }
    },
  },
  mounted() {
    if (this.showPicker && !this.show) {
      this.openPicker();
    }
  },
  methods: {
    parseModelValue(val) {
      if (this.isInternalUpdate) {
        return;
      }
      if (
        val === undefined ||
        val === null ||
        (typeof val === "string" && val.length < 5)
      ) {
        this.onlyOne = true;
        this.inString = true;
        this.internalValue = null;
        return;
      }

      if (Array.isArray(val)) {
        this.onlyOne = false;
        this.inString = false;
        const newDates = [
          dayjs(val[0]).isValid()
            ? dayjs(val[0]).utcOffset(0).toDate()
            : new Date(),
          dayjs(val[1]).isValid()
            ? dayjs(val[1]).utcOffset(0).toDate()
            : new Date(),
        ];
        // Ensure time is preserved if it was present, or set to start/end of day if not
        if (!this.showTime) {
          // Normalizing to start of day in UTC.
          newDates[0] = dayjs(newDates[0]).utcOffset(0).startOf("day").toDate();
          newDates[1] = dayjs(newDates[1]).utcOffset(0).startOf("day").toDate();
        }
        if (
          !this.internalValue ||
          !Array.isArray(this.internalValue) ||
          this.internalValue[0]?.getTime() !== newDates[0].getTime() ||
          this.internalValue[1]?.getTime() !== newDates[1].getTime()
        ) {
          this.internalValue = newDates;
        }
      } else {
        this.onlyOne = true;
        if (typeof val === "string") {
          this.inString = true;
          const parsed = dayjs(val.replace(/\./g, "-"));
          const newDate = parsed.isValid() ? parsed.toDate() : new Date();
          if (
            !this.internalValue ||
            Array.isArray(this.internalValue) ||
            (typeof this.internalValue.getTime === "function" &&
              this.internalValue.getTime() !== newDate.getTime())
          ) {
            this.internalValue = newDate;
          }
        } else {
          this.inString = false;
          const newDate = new Date(val);
          if (
            !this.internalValue ||
            Array.isArray(this.internalValue) ||
            (typeof this.internalValue.getTime === "function" &&
              this.internalValue.getTime() !== newDate.getTime())
          ) {
            this.internalValue = newDate;
          }
        }
      }
    },
    handleUpdate(val) {
      this.isInternalUpdate = true;
      if (Array.isArray(val) && Array.isArray(this.internalValue)) {
        if (
          val[0]?.getTime() === this.internalValue[0]?.getTime() &&
          val[1]?.getTime() === this.internalValue[1]?.getTime()
        ) {
          // No change, but let's make sure internalValue is updated if needed (VueDatePicker v-model should have handled it)
        } else {
          this.internalValue = val;
        }
      } else if (!Array.isArray(val) && !Array.isArray(this.internalValue)) {
        if (
          val?.getTime &&
          this.internalValue?.getTime &&
          val.getTime() === this.internalValue.getTime()
        ) {
          // No change
        } else {
          this.internalValue = val;
        }
      } else {
        this.internalValue = val;
      }

      let emitValue = val;

      if (!this.onlyOne && Array.isArray(val) && !this.showTime) {
        val[0] = dayjs(val[0]).utcOffset(0).startOf("day").toDate();
        val[1] = dayjs(val[1]).utcOffset(0).startOf("day").toDate();
      }

      if (this.onlyOne) {
        if (this.inString && val) {
          emitValue = dayjs(val).format("YYYY-MM-DD HH:mm:ss");
        }
      } else if (Array.isArray(val)) {
        emitValue = val;
      }

      // Check if emitValue is different from modelValue before emitting
      let isDifferent = false;
      if (Array.isArray(emitValue) && Array.isArray(this.modelValue)) {
        if (
          new Date(emitValue[0]).getTime() !==
            new Date(this.modelValue[0]).getTime() ||
          new Date(emitValue[1]).getTime() !==
            new Date(this.modelValue[1]).getTime()
        ) {
          isDifferent = true;
        }
      } else if (emitValue !== this.modelValue) {
        isDifferent = true;
      }

      if (isDifferent) {
        this.$emit("update:modelValue", emitValue);
        this.$emit("change");
      }

      if (this.onlyPicker && isDifferent) {
        this.show = false;
        this.$emit("update:showPicker", false);
      }

      this.$nextTick(() => {
        this.isInternalUpdate = false;
      });
    },
    onOpen() {
      // Don't emit if already open to avoid redundant updates
      if (!this.show) {
        this.show = true;
        this.$emit("update:showPicker", true);
      }
    },
    onClosed() {
      if (this.show) {
        this.show = false;
        this.$emit("update:showPicker", false);
      }
    },
    openPicker() {
      if (this.onlyPicker) return;
      if (this.$refs.datepicker) {
        this.$refs.datepicker.openMenu();
        if (!this.show) {
          this.show = true;
          this.$emit("update:showPicker", true);
        }
      }
    },
    closePicker() {
      if (this.$refs.datepicker) {
        this.$refs.datepicker.closeMenu();
        if (this.show) {
          this.show = false;
          this.$emit("update:showPicker", false);
        }
      }
    },
    format(date) {
      if (Array.isArray(date)) {
        if (!date[0] || !date[1]) return "";
        return `${dateHelper.formatFullName(
          date[0]
        )} - ${dateHelper.formatFullName(date[1])}`;
      }
      return dateHelper.formatFullName(date);
    },
  },
};
</script>
